import { Cloud, CloudOff, RefreshCw, TriangleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SYNC_STATE_LABELS } from '../../core/sync-engine';
import type { SyncState } from '../../core/sync-engine';

interface SyncStatusBadgeProps {
  state: SyncState;
  lastSyncedAt: number | null;
  /** Tapping the badge forces a pass — the manual way out of a backoff. */
  onSync: () => void;
}

const ICONS: Record<SyncState, LucideIcon> = {
  offline: CloudOff,
  idle: Cloud,
  syncing: RefreshCw,
  error: TriangleAlert,
};

const STYLES: Record<SyncState, string> = {
  offline: 'text-ink/40 border-line',
  idle: 'text-emerald-700 border-emerald-200 bg-emerald-50',
  syncing: 'text-brand-700 border-brand-100 bg-brand-50',
  error: 'text-rose-700 border-rose-200 bg-rose-50',
};

/** `16:05` — when the cloud was last agreed with. */
const clock = (at: number): string => {
  const pad = (part: number) => String(part).padStart(2, '0');
  const time = new Date(at);
  return `${pad(time.getHours())}:${pad(time.getMinutes())}`;
};

/**
 * The state of the cloud mirror, in the top bar.
 *
 * It is deliberately quiet: `offline` is a normal, working state for this
 * application — the device has all its data — so it reads as grey rather than
 * as a warning. Only a backend that answered and then repeatedly failed earns
 * the red.
 */
export function SyncStatusBadge({ state, lastSyncedAt, onSync }: SyncStatusBadgeProps) {
  const Icon = ICONS[state];
  const title =
    lastSyncedAt === null
      ? SYNC_STATE_LABELS[state]
      : `${SYNC_STATE_LABELS[state]} · עודכן ${clock(lastSyncedAt)}`;

  return (
    <button
      type="button"
      onClick={onSync}
      data-sync-state={state}
      title={title}
      aria-label={title}
      className={`h-8 px-2.5 rounded-xl border text-xs flex items-center gap-1.5 ${STYLES[state]}`}
    >
      <Icon size={14} className={state === 'syncing' ? 'animate-spin' : undefined} aria-hidden />
      <span className="hidden sm:inline">{SYNC_STATE_LABELS[state]}</span>
    </button>
  );
}
